// https://silvestri.co/blog/nextjs-tanstack-query-supabase-guide

"use client";
import {
  isServer,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { QUERY_STALE_TIME_MS } from "@/const/reactQuery";
import { isMfaRequiredError } from "@/lib/auth/mfaErrors";
import { redirectIfMfaRequired } from "@/lib/auth/mfaRequiredRedirect";

// TanStack Query
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: QUERY_STALE_TIME_MS,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) =>
          !isMfaRequiredError(error) && failureCount < 3,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (redirectIfMfaRequired(error)) return;
        const message = query.meta?.errorMessage as string | undefined;
        if (message) {
          toast.error(`${message}: ${error.message ?? "Unknown error"}`);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        redirectIfMfaRequired(error);
      },
    }),
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

export function ReactQueryClientProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
